<!-- Loading State -->
<div *ngIf="isLoading()" class="d-flex justify-content-center align-items-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Error State -->
<div *ngIf="!isLoading() && errorMessage()" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>
  {{ errorMessage() }}
  <button class="btn btn-link p-0 ms-2" (click)="goBack()">Return to Customers</button>
</div>

<!-- Content -->
<ng-container *ngIf="!isLoading() && !errorMessage() && customer()">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-1">
          <li class="breadcrumb-item"><a routerLink="/customers">Customers</a></li>
          <li class="breadcrumb-item active">{{ customer()!.name }}</li>
        </ol>
      </nav>
      <h1 class="h2 mb-0 fw-bold">{{ customer()!.name }}</h1>
    </div>
    <div class="btn-group">
      <button class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
      <button class="btn btn-outline-primary" (click)="editCustomer()">
        <i class="bi bi-pencil me-2"></i>Edit
      </button>
      <button class="btn btn-outline-danger" (click)="confirmDelete()">
        <i class="bi bi-trash me-2"></i>Delete
      </button>
    </div>
  </div>

  <div class="row g-4">
    <!-- Left Column - Main Content -->
    <div class="col-lg-8">
      <!-- Contact Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-telephone me-2"></i>Contact Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Email</label>
              <p class="mb-0">
                <ng-container *ngIf="customer()!.email; else noEmail">
                  <a [href]="'mailto:' + customer()!.email" class="text-decoration-none">
                    <i class="bi bi-envelope me-2"></i>{{ customer()!.email }}
                  </a>
                </ng-container>
                <ng-template #noEmail>
                  <span class="text-muted fst-italic">Not provided</span>
                </ng-template>
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Phone</label>
              <p class="mb-0">
                <ng-container *ngIf="customer()!.phone; else noPhone">
                  <a [href]="'tel:' + customer()!.phone" class="text-decoration-none">
                    <i class="bi bi-telephone me-2"></i>{{ customer()!.phone | phoneFormat }}
                  </a>
                </ng-container>
                <ng-template #noPhone>
                  <span class="text-muted fst-italic">Not provided</span>
                </ng-template>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Address -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Address</h5>
          <a *ngIf="hasAddress()" [href]="getMapsUrl()" target="_blank"
             class="btn btn-outline-secondary btn-sm">
            <i class="bi bi-map me-1"></i>View on Map
          </a>
        </div>
        <div class="card-body">
          <ng-container *ngIf="hasAddress(); else noAddress">
            <p class="mb-0">
              <span *ngIf="customer()!.address">{{ customer()!.address }}<br></span>
              <span>
                {{ customer()!.city }}<span *ngIf="customer()!.city && customer()!.state">, </span>{{ customer()!.state }}
                <span *ngIf="customer()!.zip"> {{ customer()!.zip }}</span>
              </span>
            </p>
          </ng-container>
          <ng-template #noAddress>
            <p class="mb-0 text-muted fst-italic">No address provided</p>
          </ng-template>
        </div>
      </div>

      <!-- Fields -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-geo-alt-fill me-2"></i>Fields</h5>
          <button class="btn btn-primary btn-sm" (click)="addField()">
            <i class="bi bi-plus-lg me-1"></i>Add Field
          </button>
        </div>
        <div class="card-body">
          <ng-container *ngIf="customerFields().length > 0; else noFields">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Acreage</th>
                    <th>Crop Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let field of customerFields()">
                    <td>
                      <a [routerLink]="['/logbook/fields', field.id, 'edit']" class="text-decoration-none">
                        {{ field.name }}
                      </a>
                      <i *ngIf="field.agremoFieldId"
                         class="bi bi-leaf text-success ms-1 small"
                         title="Synced to Agremo"></i>
                      <i *ngIf="field.agremoFieldId && !field.hasBoundary"
                         class="bi bi-exclamation-triangle text-warning ms-1 small"
                         title="Synced to Agremo but no boundary defined"></i>
                      <i *ngIf="field.hasBoundary"
                         class="bi bi-bounding-box text-info ms-1 small"
                         title="Boundary defined"></i>
                    </td>
                    <td>{{ field.acreage | number:'1.1-1' }} ac</td>
                    <td>
                      <span class="badge bg-success-subtle text-success">{{ field.cropType }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>
          <ng-template #noFields>
            <div class="text-center py-4">
              <div class="text-muted mb-3">
                <i class="bi bi-geo-alt fs-1"></i>
              </div>
              <p class="text-muted mb-3">No fields for this customer yet.</p>
              <button class="btn btn-outline-primary btn-sm" (click)="addField()">
                <i class="bi bi-plus-lg me-1"></i>Add First Field
              </button>
            </div>
          </ng-template>
        </div>
      </div>

      <!-- Notes -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Notes</h5>
        </div>
        <div class="card-body">
          <p class="mb-0" *ngIf="customer()!.notes; else noNotes">{{ customer()!.notes }}</p>
          <ng-template #noNotes>
            <p class="mb-0 text-muted fst-italic">No notes</p>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Quick Stats -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Quick Stats</h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Fields</span>
            <span class="fw-medium">{{ fieldCount() }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Total Acreage</span>
            <span class="fw-medium">{{ totalAcreage() | number:'1.0-0' }} ac</span>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Added</span>
            <span class="fw-medium">{{ customer()!.createdAt | adnDate }}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button class="btn btn-outline-primary" (click)="editCustomer()">
              <i class="bi bi-pencil me-2"></i>Edit Customer
            </button>
            <button class="btn btn-outline-secondary" (click)="addField()">
              <i class="bi bi-plus-lg me-2"></i>Add Field
            </button>
            <button class="btn btn-outline-secondary" (click)="goBack()">
              <i class="bi bi-arrow-left me-2"></i>Back to Customers
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
